'use client';

import { useRef, useEffect } from 'react';
import Link from 'next/link';
import { gsap } from '@/lib/gsap';

const termsContent = [
  {
    id: 'acceptance',
    title: 'Acceptance of Terms',
    content: `By accessing or using our Services, you agree to these Terms, our Privacy Policy, and any other policies referenced herein. If you do not agree to these Terms, please do not use our Services.`,
  },
  {
    id: 'services',
    title: 'Services Provided',
    content: `DataCode offers a range of digital solutions including: Web and mobile app development, AI and machine learning solutions, Custom software development, SaaS products, Digital transformation tools, and Consulting services.`,
  },
  {
    id: 'eligibility',
    title: 'Eligibility',
    content: `You must be at least 18 years old or have the consent of a legal guardian to use our Services. By using our Services, you represent and warrant that you meet these eligibility requirements.`,
  },
  {
    id: 'use-of-services',
    title: 'Use of Services',
    content: `You agree not to: Use our Services for fraudulent, malicious, or illegal activities. Interfere with or disrupt the operation of our Services. Copy, redistribute, or reverse-engineer any part of our Services without prior written consent. Upload or transmit harmful content, including viruses or malicious code.`,
  },
  {
    id: 'intellectual-property',
    title: 'Intellectual Property',
    content: `All content, trademarks, logos, code, and intellectual property on our platform are owned by DataCode or its licensors. You may not: Reproduce, distribute, modify, or create derivative works without our written consent.`,
  },
  {
    id: 'user-content',
    title: 'User-Generated Content',
    content: `By submitting content through our Services, you grant DataCode a worldwide, non-exclusive, royalty-free, perpetual license to use, reproduce, modify, and distribute the content. You represent that you have all necessary rights to submit the content and that it does not infringe on third-party rights.`,
  },
  {
    id: 'third-party',
    title: 'Third-Party Integrations and Links',
    content: `Our Services may include integrations with or links to third-party platforms. DataCode does not endorse or take responsibility for third-party content or services. Your use of third-party services is governed by their respective terms.`,
  },
  {
    id: 'ai-solutions',
    title: 'AI Solutions and Limitations',
    content: `Our AI-powered solutions are designed to enhance your productivity and decision-making. However, AI outputs should not be used as the sole basis for critical or life-impacting decisions. DataCode is not responsible for errors, inaccuracies, or unintended consequences arising from the use of AI tools.`,
  },
  {
    id: 'data-privacy',
    title: 'Data Privacy and Security',
    content: `We collect and process data as described in our Privacy Policy. By using our Services, you consent to such data practices. While we take measures to secure your data, no system is entirely secure, and we cannot guarantee absolute data security.`,
  },
  {
    id: 'disclaimer',
    title: 'Disclaimer of Warranties',
    content: `Our Services are provided "as is" and "as available," without warranties of any kind, either express or implied. DataCode does not guarantee that our Services will be error-free, secure, or uninterrupted.`,
  },
  {
    id: 'liability',
    title: 'Limitation of Liability',
    content: `To the maximum extent permitted by law, DataCode shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of our Services. Our total liability shall not exceed the amount you paid for our Services.`,
  },
  {
    id: 'indemnification',
    title: 'Indemnification',
    content: `You agree to indemnify and hold harmless DataCode, its affiliates, and their respective officers, directors, employees, and agents from any claims, liabilities, damages, or expenses arising from: Your use of our Services. Your violation of these Terms. Any third-party claims related to your content or actions.`,
  },
  {
    id: 'modifications',
    title: 'Modifications to Services and Terms',
    content: `DataCode reserves the right to modify, suspend, or discontinue any part of our Services at any time. We may also update these Terms periodically. Your continued use of our Services after changes constitutes acceptance of the updated Terms.`,
  },
  {
    id: 'termination',
    title: 'Termination',
    content: `DataCode may terminate or suspend your access to our Services without prior notice if you violate these Terms or engage in behavior that harms our platform, other users, or our reputation.`,
  },
  {
    id: 'governing-law',
    title: 'Governing Law',
    content: `These Terms are governed by the laws of Iraq. Any disputes arising from these Terms shall be resolved in the courts of Kurdistan, Iraq.`,
  },
];

export function TermsPageClient() {
  const pageRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Hover effect for title letters
  const applyHoverEffect = (letters: NodeListOf<Element>) => {
    const spansArray = Array.from(letters);

    const handleMouseMove = (e: Event) => {
      const mouseEvent = e as MouseEvent;
      const hoveredSpan = mouseEvent.target as HTMLElement;
      const rect = hoveredSpan.getBoundingClientRect();
      const mouseX = mouseEvent.clientX - rect.left;
      const scaleFactor = 0.2;
      const center = rect.width / 2;

      let scale: number;
      if (mouseX < center) {
        scale = (scaleFactor + 1) + (scaleFactor * mouseX) / center;
      } else {
        scale = (scaleFactor + 1) + (scaleFactor * (rect.width - mouseX)) / center;
      }

      gsap.to(hoveredSpan, { scaleY: scale, duration: 0.5, ease: 'power4.out' });

      const hoveredIndex = spansArray.indexOf(hoveredSpan);
      const prevSpan = spansArray[hoveredIndex - 1];
      const nextSpan = spansArray[hoveredIndex + 1];

      if (prevSpan) {
        const dist = Math.min(Math.abs(rect.left - mouseEvent.clientX), center);
        const scalePrev = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(prevSpan, { scaleY: scalePrev, duration: 0.5, ease: 'power4.out' });
      }

      if (nextSpan) {
        const dist = Math.min(Math.abs(rect.right - mouseEvent.clientX), center);
        const scaleNext = 1 + (scaleFactor * (center - dist)) / center;
        gsap.to(nextSpan, { scaleY: scaleNext, duration: 0.5, ease: 'power4.out' });
      }
    };

    const handleMouseLeave = () => {
      spansArray.forEach((span) => {
        gsap.to(span, { scaleY: 1, duration: 0.5, ease: 'power4.out' });
      });
    };

    spansArray.forEach((span) => {
      span.addEventListener('mousemove', handleMouseMove);
      span.addEventListener('mouseleave', handleMouseLeave);
    });
  };

  useEffect(() => {
    if (!pageRef.current) return;

    const ctx = gsap.context(() => {
      // Animate title letters - same as home page
      const letters = titleRef.current?.querySelectorAll('.letter');
      if (letters) {
        gsap.set(letters, { scaleY: 0.3, opacity: 0 });

        const shuffledLetters = Array.from(letters).sort(() => Math.random() - 0.5);
        const letterTimeline = gsap.timeline({ delay: 0.5 });

        shuffledLetters.forEach((letter, index) => {
          letterTimeline.to(letter, {
            duration: 0.6,
            scaleY: 1,
            opacity: 1,
            ease: 'power3.out',
          }, index * 0.05);
        });

        // Apply hover effect after animation
        letterTimeline.call(() => {
          applyHoverEffect(letters);
        });
      }

      // Animate content sections
      if (contentRef.current) {
        const sections = contentRef.current.querySelectorAll('.terms-section');
        sections.forEach((section, index) => {
          gsap.set(section, { y: 30, opacity: 0 });
          gsap.to(section, {
            scrollTrigger: {
              trigger: section,
              start: 'top 90%',
            },
            y: 0,
            opacity: 1,
            duration: 0.5,
            delay: index * 0.03,
            ease: 'power3.out',
          });
        });
      }
    }, pageRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={pageRef} style={{ backgroundColor: '#0c0c0c' }}>
      {/* Hero Section */}
      <section
        className="relative flex flex-col items-center justify-center text-center"
        style={{
          backgroundColor: '#0c0c0c',
          paddingTop: '180px',
          paddingBottom: '100px',
        }}
      >
        <div className="content-padding w-full">
          <h1
            ref={titleRef}
            className="hero-title font-six-caps text-[calc(1rem+20vw)] leading-[0.9] text-white lowercase"
          >
            {'TERMS'.split('').map((char, i) => (
              <span
                key={i}
                className="letter inline-block"
                style={{ transformOrigin: 'center bottom' }}
              >
                {char}
              </span>
            ))}
          </h1>
        </div>

        {/* Description and date - Left aligned */}
        <div className="content-padding w-full" style={{ marginTop: '80px' }}>
          <div className="max-w-4xl mx-auto">
            <div className="max-w-md">
              <p className="text-white/50 text-lg leading-relaxed text-left">
                Please read these Terms of Service carefully before using DataCode.app services.
              </p>
              <p className="text-white/30 text-sm text-left mt-4">
                Effective Date: October 10, 2024
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section style={{ backgroundColor: '#0c0c0c', paddingBottom: '80px' }}>
        <div className="content-padding">
          <div className="max-w-3xl mx-auto" ref={contentRef}>

            {/* Introduction */}
            <div className="terms-section pb-12 mb-12" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <h2 className="text-2xl font-semibold text-white mb-4">
                Terms of Service
              </h2>
              <p className="text-white/60 text-lg leading-relaxed">
                Welcome to DataCode.app. These Terms of Service govern your access to and use of our website, products, and services. By using our services, you agree to be bound by these Terms and our Privacy Policy.
              </p>
            </div>

            {/* Terms Sections */}
            {termsContent.map((section, index) => (
              <div
                key={section.id}
                id={section.id}
                className="terms-section pb-10 mb-10"
                style={{
                  borderBottom: index < termsContent.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                }}
              >
                <div className="flex items-start gap-4">
                  <span
                    className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold"
                    style={{ backgroundColor: '#173835', color: '#fff' }}
                  >
                    {index + 1}
                  </span>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-white mb-3">
                      {section.title}
                    </h2>
                    <p className="text-white/60 leading-relaxed">
                      {section.content}
                    </p>
                  </div>
                </div>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* Contact Section - Green Background, Centered */}
      <section
        style={{
          backgroundColor: '#173835',
          paddingTop: '100px',
          paddingBottom: '100px',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          <h2
            className="font-six-caps text-[calc(1rem+10vw)] md:text-[calc(1rem+8vw)] leading-[0.9] text-white lowercase"
            style={{ textAlign: 'center', marginBottom: '32px' }}
          >
            Contact Us
          </h2>
          <p
            className="text-white/70 text-lg leading-relaxed"
            style={{ textAlign: 'center', maxWidth: '500px', marginBottom: '48px', padding: '0 24px' }}
          >
            If you have any questions about these Terms of Service, please contact us.
          </p>

          {/* Contact Info - Centered */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '24px',
              marginBottom: '48px',
            }}
            className="sm:flex-row sm:gap-10"
          >
            <a
              href="mailto:info@datacode.app"
              className="flex items-center gap-3 text-white hover:text-white/80 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>info@datacode.app</span>
            </a>

            <span className="hidden sm:block text-white/30">|</span>

            <div className="flex items-center gap-3 text-white/70">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Iraq, Erbil, MRF B2, apt80</span>
            </div>
          </div>

          {/* Back to Home - Centered */}
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-white/50 hover:text-white transition-colors text-sm uppercase tracking-wider"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>
        </div>
      </section>
    </div>
  );
}
